import { onBeforeUnmount, onMounted, onUnmounted, type Ref } from 'vue';
import { Modal } from 'bootstrap';

interface UseModalOptions {
  onShow?: () => void;
  onShown?: () => void;
  onHidden?: () => void;
}

const SHOW = 'show.bs.modal';
const SHOWN = 'shown.bs.modal';
const HIDE = 'hide.bs.modal';
const HIDDEN = 'hidden.bs.modal';

/**
 * A Bootstrap modal that a component renders: show() and hide() for the component, and the
 * component told when the modal starts to open, has opened, and has closed. A modal opened by
 * Bootstrap's data API (a data-bs-toggle="modal" button) reports the same way.
 *
 * Bootstrap's modal events bubble, so a modal nested in this one's element reports its own
 * through it. Only events whose target is this modal count.
 *
 * The modal is torn down with its component, open or not, and the owner is not told about the
 * close that causes: it is the one leaving. Bootstrap's show() locks the page — `modal-open` and
 * `overflow: hidden` on <body>, and a backdrop appended to <body> — and only the completion of
 * hide() gives it back. Vue detaches the modal's element on unmount, and with it Bootstrap's
 * Escape and backdrop-click listeners, so a modal left open covers the next page until a reload.
 *
 * The teardown is teardownOffcanvas' (useOffcanvas.ts) — hide, and dispose only once Bootstrap
 * reports the modal hidden — but a Modal needs more than the moment of unmount to know what is
 * pending:
 *  - hide() is ignored while a show is running, so a show caught at unmount is waited out first.
 *    Caught before its dialog appeared, that show's completion puts the detached element back on
 *    <body>, so the element is kept out of sight until then and removed once the teardown is done.
 *  - A hide completes in two steps, the modal's fade and then the backdrop's. Between them
 *    Bootstrap's own state reads as closed, though the completion that gives the page back is
 *    still to run; disposing then makes it throw half way, with the page still locked.
 * So the composable follows the modal's events from mount: a show or a hide counts as running
 * from its `show`/`hide` event, unless that was cancelled, until its `shown`/`hidden`.
 *
 * A static modal's bounce (its answer to a click on its backdrop, or to Escape without `keyboard`)
 * announces itself with `hidePrevented` but reports no end, and nothing waits for it: by
 * Bootstrap's transition times it is over before the teardown's own close, unless the owner
 * unmounts within a few milliseconds of it starting. Then its last step can throw after the
 * dispose, once the page has been given back.
 *
 * Limits, none of them reached by the current owners: a hide that another listener cancels is not
 * forced, so that modal is disposed still open; the element is the one rendered at mount, so it
 * must not come and go under a v-if; and KeepAlive's deactivation runs no unmount hook.
 */
export function useModal(elRef: Ref<HTMLElement | null>, options: UseModalOptions = {}) {
  // The teardown outlives the template ref, which Vue nulls on unmount.
  let el: HTMLElement | null = null;
  let showing: Event | null = null;
  let hiding: Event | null = null;
  let leaving = false;
  let unmounted = false;

  const show = () => {
    if (elRef.value) {
      Modal.getOrCreateInstance(elRef.value).show();
    }
  };

  const hide = () => {
    if (elRef.value) {
      Modal.getInstance(elRef.value)?.hide();
    }
  };

  const running = (event: Event | null) => event !== null && !event.defaultPrevented;

  const callbacks: Record<string, (() => void) | undefined> = {
    [SHOW]: options.onShow,
    [SHOWN]: options.onShown,
    [HIDDEN]: options.onHidden,
  };

  const onEvent = (event: Event) => {
    if (!el || event.target !== el) return;

    if (event.type === SHOW) showing = event;
    else if (event.type === SHOWN) showing = null;
    else if (event.type === HIDE) hiding = event;
    else if (event.type === HIDDEN) hiding = null;

    if (!leaving) {
      callbacks[event.type]?.();
    }
    else if (event.type === SHOWN || event.type === HIDDEN) {
      // Look again after a task, once this event's other listeners have run: dispose()
      // unregisters every Bootstrap listener on the element, including one that may still be
      // waiting for this `hidden` — the data API's, which gives focus back to the button that
      // opened the modal.
      setTimeout(teardown);
    }
  };

  const release = () => {
    for (const type of [SHOW, SHOWN, HIDE, HIDDEN]) el?.removeEventListener(type, onEvent);
    // Still in the page after the owner has gone only if a show that outlived it put it back.
    if (unmounted && el?.isConnected) el.remove();
  };

  // Runs at unmount, and again each time a show or a hide it is waiting on reports in.
  const teardown = () => {
    const instance = el && Modal.getInstance(el);
    if (!el || !instance) return release();
    if (running(showing)) {
      // Out of sight, and so out of reach: the show may yet put the element back on <body>.
      el.style.visibility = 'hidden';
      return;
    }
    instance.hide(); // ignored unless the modal is on screen with nothing running
    if (running(hiding)) return;
    instance.dispose();
    release();
  };

  onMounted(() => {
    el = elRef.value;
    for (const type of [SHOW, SHOWN, HIDE, HIDDEN]) el?.addEventListener(type, onEvent);
  });

  // onBeforeUnmount, not onUnmounted: the hide has to start while the element is still in the
  // page. Bootstrap times each step from the element's CSS transition, and a detached element has
  // none, so a close started after Vue has detached it would no longer outlast a bounce (see above).
  onBeforeUnmount(() => {
    leaving = true;
    teardown();
  });

  onUnmounted(() => {
    unmounted = true;
  });

  return { show, hide };
}
